export enum StorageType {
  localStorage = 'localStorage',
  sessionStorage = 'sessionStorage',
}

interface StorageObject<Type> {
  [key: string]: Type;
}

interface StorageOptions {
  type: StorageType;
}

const defaultOptions: StorageOptions = {
  type: StorageType.localStorage,
};

// Resolve the storage object lazily on every access. The cookie fallback in
// `checkLocalStorage` replaces `window.localStorage`/`window.sessionStorage` at
// runtime, so we must never cache a reference to the (possibly null) native
// storage captured at module-evaluation time.
function getStorage(type: StorageType): Storage {
  return type === StorageType.sessionStorage ? globalThis.sessionStorage : globalThis.localStorage;
}

type GetItemOptions<Type> = Partial<StorageOptions> & { defaultValue: Type };

export function getItem<Type>(key: string, options: GetItemOptions<Type>) {
  const type = options?.type ?? defaultOptions.type;
  const { defaultValue } = options;
  try {
    const item = getStorage(type).getItem(key);
    return item ? (JSON.parse(item) as Type) : defaultValue;
  } catch (e) {
    console.error(e);
    return defaultValue;
  }
}

export function getSubItem<Type>(key: string, subKey: string, options: GetItemOptions<Type>) {
  const type = options?.type ?? defaultOptions.type;
  const { defaultValue } = options;
  try {
    const item = getStorage(type).getItem(key);
    return item
      ? ((JSON.parse(item) as StorageObject<Type>)?.[subKey] ?? defaultValue)
      : defaultValue;
  } catch (e) {
    console.error(e);
    return defaultValue;
  }
}

export function removeItem(key: string, options = defaultOptions): void {
  try {
    getStorage(options.type).removeItem(key);
  } catch (e) {
    console.error(e);
  }
}

export function setItem<Type>(key: string, value: Type, options = defaultOptions): void {
  try {
    getStorage(options.type).setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(e);
  }
}

export function setSubItem<Type>(
  key: string,
  subKey: string,
  value: Type,
  options = defaultOptions,
): void {
  try {
    const item: StorageObject<Type> = getItem<Type>(key, { defaultValue: undefined as any }) ?? {};
    item[subKey] = value;

    getStorage(options.type).setItem(key, JSON.stringify(item));
  } catch (e) {
    console.error(e);
  }
}

/**
 * Returns true if the browser has native (non-shimmed) localStorage.
 * On Xi6 Flex STBs, localStorage is polyfilled with an in-memory shim marked
 * with `__shim`. Code that would crash on the shim (e.g. Okta's signOut) can
 * use this to branch into a safe fallback.
 */
export function checkNativeStorage(): boolean {
  try {
    const key = '__storage_test__';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return !(window.localStorage as any)?.__shim;
  } catch {
    return false;
  }
}
